// dependencies
import {
	CategoryScale,
	Chart,
	Chart as ChartJS,
	LineElement,
	LinearScale,
	PointElement,
	Title,
	Tooltip,
} from "chart.js";
import { startOfMonth } from "date-fns";
import React, { MutableRefObject, useEffect, useRef, useState } from "react";
import { Line, getElementAtEvent } from "react-chartjs-2";

import { Spinner } from "@performyard/py-components";

import EmptyChart from "../../../../../../components/EmptyChart";
import Text from "../../../../../../components/Text";
// components
import { CardBody, CardHeader } from "../../../../components/Card";
import CohortSelect from "../../../../components/CohortSelect";
// styles
import css from "../../../../components/SatisfactionTrendlineChart/SatisfactionTrendline.module.scss";
// api hooks
import useCohortsSatisfaction from "../../../../components/SatisfactionTrendlineChart/useSatisfactionTrendlineChart";

// store
import useStore from "../../../../store";

import { useCohorts, usePageSettings, useUpdatePageSettings } from "../../../../../../api/hooks";

// schema
import { ICohortModel } from "../../../../../../api/services";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip);

// constants
const MAX_COHORTS = 4;

const CohortsSatisfaction = () => {
	const [selectedCohorts, setSelectedCohorts] = useState<ICohortModel[]>([]);
	const [selectedCohortIds, setSelectedCohortIds] = useState<string[]>([]);
	const [filter, month, cohort, setMonth, setCohort] = useStore((state) => [
		state.filter,
		state.cohorts.month,
		state.cohorts.cohort,
		state.cohorts.setMonth,
		state.cohorts.setCohort,
	]);

	// API hooks
	const { isLoading: isCohortsLoading, data: cohortsData } = useCohorts();

	const pageSettingsOnSuccess = (onSuccessData) => {
		const newSelectedCohortIds = onSuccessData?.cohorts?.trendline?.selected_cohorts || [];
		const newSelectedCohorts = cohortsData?.cohorts.filter((c) => newSelectedCohortIds.includes(c._id)) || [];
		setSelectedCohorts(newSelectedCohorts);
		setSelectedCohortIds(newSelectedCohortIds);
	};

	const setPageSettings = useUpdatePageSettings(pageSettingsOnSuccess).mutate;

	// state for selected cohorts
	const {
		data: pageSettingsData,
		refetch: refetchPageSettings,
		isLoading: isPageSettingsLoading,
		isSuccess,
	} = usePageSettings();

	useEffect(() => {
		if (isSuccess) {
			pageSettingsOnSuccess(pageSettingsData);
		}
	}, [isPageSettingsLoading]);

	// chart ref
	const chartRef = useRef<ChartJS>();

	// clear selectedDataPoint value when filter changes
	useEffect(() => {
		setMonth(null);
	}, [filter.value]);

	// refetch page settings when cohorts have loaded
	useEffect(() => {
		if (!isCohortsLoading) {
			refetchPageSettings();
		}
	}, [isCohortsLoading]);

	// get data for line chart
	const { data, legendItems, options, dateRangeLabels, isLoading, indexOfSelectedMonth, indexOfSelectedCohort } =
		useCohortsSatisfaction(filter, month, cohort, selectedCohorts);

	let hasSufficientData = false;

	// check if chart has sufficient data
	if (data.datasets[0].data && data.datasets[0].data.length) {
		const dataSetData = data.datasets[0].data;
		// data exists, set sufficient to true
		if (dataSetData.length) {
			hasSufficientData = true;
		}
	}

	// handle selection of cohorts
	const handleAddCohort = (cohort: string) => {
		const cohortModel = cohortsData?.cohorts.find((c) => c._id === cohort);
		if (selectedCohorts.length < MAX_COHORTS && cohortModel?._id) {
			setPageSettings({
				settings_path: "cohorts.trendline.selected_cohorts",
				settings_value: [...selectedCohortIds, cohortModel._id],
			});
		}
	};

	// handle removal of cohort
	const handleCohortRemove = (cohortID: string) => {
		setPageSettings({
			settings_path: "cohorts.trendline.selected_cohorts",
			settings_value: selectedCohortIds.filter((c) => c !== cohortID),
		});
		if (cohortID === cohort) {
			setCohort(null);
		}
	};

	// handles clicking on a data point on the line chart
	const onClickChart = (event) => {
		const monthClicked = chartRef.current && getElementAtEvent(chartRef.current, event)[0]?.index;

		if (typeof monthClicked === "number") {
			const dataSet = chartRef.current && getElementAtEvent(chartRef.current, event)[0]?.datasetIndex;

			// if a cohort is selected, don't allow clicking on other dataset points
			if (cohort && dataSet !== indexOfSelectedCohort) {
				return;
			}

			if (monthClicked === indexOfSelectedMonth && dataSet === indexOfSelectedCohort) {
				setMonth(null);
				setCohort(null);
			} else {
				// defaults to first day of the month (year-month-1)
				const formattedMonth = startOfMonth(new Date(`${dateRangeLabels[monthClicked].value}-1`));

				setMonth(formattedMonth);

				if (dataSet === 0) {
					setCohort("all");
				} else if (dataSet && dataSet !== indexOfSelectedCohort) {
					const selectedCohort = selectedCohorts[dataSet - 1];
					setCohort(selectedCohort._id);
				}
			}
		}
	};

	const handleCohortSelect = (cohortID: string) => {
		if (cohortID === cohort) {
			setCohort(null);
			setMonth(null);
		} else {
			setCohort(cohortID);
		}
	};

	return (
		<>
			<CardHeader
				title={
					<span>
						<Text element="span" size="paragraph-2" weight="semi-bold">
							Satisfaction Trendline
						</Text>
					</span>
				}
				info="This chart displays the overall satisfaction of all employees during  the selected period."
			/>
			<CardBody>
				<div className={css.cardBody}>
					{isLoading && (
						<div className={css.spinnerContainer}>
							<Spinner size={20} />
						</div>
					)}
					{!isLoading && !hasSufficientData && (
						<div className={css.emptyChartContainer}>
							<EmptyChart type="trendline" />
						</div>
					)}
					{!isLoading && hasSufficientData && (
						<div>
							<div className={css.trendlineChartContainer}>
								<div className={`${css.trendChart}`}>
									<Line
										ref={chartRef as MutableRefObject<Chart<"line", any, unknown>>}
										options={options as any}
										data={data}
										onClick={onClickChart}
									/>
								</div>
							</div>
							<CohortSelect
								selectedCohorts={selectedCohorts}
								handleAddCohort={handleAddCohort}
								handleCohortRemove={handleCohortRemove}
								handleCohortSelect={handleCohortSelect}
								legendItems={legendItems}
								maxCohorts={MAX_COHORTS}
								cohort={cohort}
							/>
						</div>
					)}
				</div>
			</CardBody>
		</>
	);
};

export default CohortsSatisfaction;
